import { HttpClient, HttpHeaders } from "@angular/common/http";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";

interface CreateSecurityGroupRuleRow {
  id: string;
  protocol: string;
  port: string;
  target: string;
  description: string;
}

type SecurityGroupRuleTargetType =
  | "cidr_ipv4"
  | "cidr_ipv6"
  | "referenced_group_ids"
  | "prefix_list_ids";

interface SecurityGroupRulePayloadRow {
  protocol: string;
  from_port: number | null;
  to_port: number | null;
  target_type: SecurityGroupRuleTargetType;
  target_value: string;
}

interface SecurityGroupApiRulePayload {
  ip_protocol: string;
  from_port?: number;
  to_port?: number;
  cidr_ipv4: string[];
  cidr_ipv6: string[];
  prefix_list_ids: string[];
  referenced_group_ids: string[];
}

interface CreateSecurityGroupRulesUpdatePayload {
  account_id: string;
  region: string;
  authorize_ingress: SecurityGroupApiRulePayload[];
  revoke_ingress: SecurityGroupApiRulePayload[];
  authorize_egress: SecurityGroupApiRulePayload[];
  revoke_egress: SecurityGroupApiRulePayload[];
}

interface CreateSecurityGroupFormModel {
  group_name: string;
  description: string;
  vpc_id: string;
  environment: string;
  inbound_rules: CreateSecurityGroupRuleRow[];
  outbound_rules: CreateSecurityGroupRuleRow[];
}

@Component({
  selector: "app-request-eks-modal",
  templateUrl: "./request-eks-modal.component.html",
  styleUrls: [
    "../eks-requests.styles.css",
    "../../../launch-ec2/launch-ec2.operations.component.css",
  ],
})
export class RequestEksModalComponent implements OnChanges {
  @ViewChild("eksRequestModalBody") eksRequestModalBody?: ElementRef<HTMLDivElement>;
  @ViewChild("securityGroupModalBody") securityGroupModalBody?: ElementRef<HTMLDivElement>;
  @Input() visible = false;
  @Input() accountId = "";
  @Input() region = "";
  @Input() apiBaseUrl = "";
  @Input() urlPrefix = "";
  @Output() close = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<void>();

  step = 1;
  loading: any = {};
  options: any = { vpcs: [], subnets: [], securityGroups: [], kubernetesVersions: [], clusterRoles: [] };
  clusterPricing: any = null;
  pricingError = "";
  formError = "";
  saveError = "";
  saveSuccessMessage = "";

  showCreateSecurityGroupForm = false;
  createSecurityGroupSubmitAttempted = false;
  createSecurityGroupStep = 1;
  createSecurityGroupForm: CreateSecurityGroupFormModel = this.getDefaultCreateSecurityGroupForm();
  readonly createSecurityGroupStepLabels: string[] = ["Basic", "Inbound", "Outbound", "Tags", "Review"];
  readonly createSecurityGroupProtocolOptions: string[] = ["TCP", "UDP", "ICMP", "All"];
  readonly createSecurityGroupEnvironmentOptions: string[] = ["prod", "dev", "qa", "uat", "stage"];
  private createSecurityGroupRuleSequence = 0;

  steps = [
    { id: 1, label: "Details" },
    { id: 2, label: "Network" },
    { id: 3, label: "Access" },
    { id: 4, label: "Tags" },
    { id: 5, label: "Review" },
  ];

  form: any = this.defaultForm();

  constructor(
    private apiServer: APIService,
    private notifier: NotifierService,
    private http: HttpClient
  ) {}

  onDropdownOpen(select: NgSelectComponent, container: "request" | "securityGroup") {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.ensureDropdownVisible(select, container);
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["visible"] && this.visible) {
      this.resetModal();
      this.loadMasters();
    }
  }

  private defaultForm(): any {
    return {
      cluster_name: "",
      launch_justification: "",
      vpc_id: null,
      cluster_role_arn: null,
      subnet_ids: [],
      security_group_ids: [],
      kubernetes_version: null,
      endpoint_public_access: true,
      endpoint_private_access: false,
      tags: [{ key: "", value: "" }],
    };
  }

  private createSecurityGroupRule(
    direction: "inbound" | "outbound",
    overrides: Partial<CreateSecurityGroupRuleRow> = {}
  ): CreateSecurityGroupRuleRow {
    this.createSecurityGroupRuleSequence += 1;

    const defaults =
      direction === "inbound"
        ? {
            protocol: "TCP",
            port: "443",
            target: "0.0.0.0/0",
            description: "HTTPS",
          }
        : {
            protocol: "All",
            port: "-",
            target: "0.0.0.0/0",
            description: "Allow all outbound",
          };

    return {
      id: `sg-rule-${direction}-${this.createSecurityGroupRuleSequence}`,
      ...defaults,
      ...overrides,
    };
  }

  private getDefaultCreateSecurityGroupForm(vpcId: string = ""): CreateSecurityGroupFormModel {
    return {
      group_name: "",
      description: "",
      vpc_id: vpcId || this.form?.vpc_id || "",
      environment: "prod",
      inbound_rules: [this.createSecurityGroupRule("inbound")],
      outbound_rules: [this.createSecurityGroupRule("outbound")],
    };
  }

  private resetModal() {
    this.step = 1;
    this.form = this.defaultForm();
    this.options = { vpcs: [], subnets: [], securityGroups: [], kubernetesVersions: [], clusterRoles: [] };
    this.clusterPricing = null;
    this.pricingError = "";
    this.formError = "";
    this.saveError = "";
    this.saveSuccessMessage = "";
    this.loading = {};
    this.showCreateSecurityGroupForm = false;
    this.createSecurityGroupSubmitAttempted = false;
    this.createSecurityGroupStep = 1;
    this.createSecurityGroupForm = this.getDefaultCreateSecurityGroupForm();
  }

  private normalize(value: any): string {
    const v = (value ?? "").toString().trim();
    return !v || v.toLowerCase() === "null" || v.toLowerCase() === "undefined" ? "" : v;
  }

  private headers() {
    return { Authorization: localStorage.getItem("t") };
  }

  private getPostHeaders() {
    let headers = new HttpHeaders().set("Content-Type", "application/json; charset=utf-8");
    const token = localStorage.getItem("t");
    if (token) {
      headers = headers.set("Authorization", token);
    }
    return headers;
  }

  private getPutHeaders() {
    let headers = new HttpHeaders().set("Content-Type", "application/json; charset=utf-8");
    const token = localStorage.getItem("t");
    if (token) {
      headers = headers.set("Authorization", token);
    }
    return headers;
  }

  private buildApiUrl(path: string) {
    return `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
  }

  private wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async get(url: string) {
    try {
      const r = await this.apiServer.getDataPromis(url, {}, this.headers());
      return typeof r === "string" ? null : r;
    } catch {
      return null;
    }
  }

  private async post(url: string, body: any) {
    try {
      const r = await this.http.post<any>(url, body, { headers: this.getPostHeaders() }).toPromise();
      if (!r || typeof r === "string") {
        return null;
      }
      return r;
    } catch {
      return null;
    }
  }

  private async put(url: string, body: any) {
    try {
      return await this.http.put<any>(url, body, { headers: this.getPutHeaders() }).toPromise();
    } catch (error: any) {
      return error?.error || null;
    }
  }

  private isSuccessResponse(response: any): boolean {
    return !!(
      response?.status === "1" ||
      response?.s === "1" ||
      response?.group_id ||
      response?.security_group_id ||
      response?.data?.group_id
    );
  }

  private getRequesterId(): string {
    return this.normalize(localStorage.getItem("eId"));
  }

  private getRequesterEmail(): string {
    return (
      this.normalize(localStorage.getItem("email")) ||
      this.normalize(localStorage.getItem("userEmail")) ||
      this.normalize(localStorage.getItem("eId"))
    );
  }

  async loadMasters() {
    if (!this.accountId || !this.region) return;
    await Promise.all([this.loadVpcs(), this.loadKubernetesVersions(), this.loadClusterRoles()]);
  }

  async loadVpcs() {
    this.loading.vpcs = true;
    const url = this.buildApiUrl(`apis/ec2-config-2/api/ec2/vpcs?account_id=${this.accountId}&region=${this.region}`);
    const r = await this.get(url);
    this.options.vpcs = r?.vpcs || [];
    this.loading.vpcs = false;
  }

  async loadKubernetesVersions() {
    this.loading.kubernetesVersions = true;
    const url = this.buildApiUrl(
      `apis/eks-master-data/api/eks/kubernetes-versions?account_id=${this.accountId}&region=${this.region}`
    );
    const r = await this.get(url);
    this.options.kubernetesVersions = r?.kubernetes_versions || [];
    this.loading.kubernetesVersions = false;
  }

  async loadClusterRoles() {
    this.loading.clusterRoles = true;
    const url = this.buildApiUrl(
      `apis/eks-master-data/api/eks/cluster-roles?account_id=${this.accountId}&region=${this.region}&service_name=eks.amazonaws.com`
    );
    const r = await this.get(url);
    const roles = r?.cluster_roles || [];
    this.options.clusterRoles = roles.filter((x: any) => !this.isServiceLinkedRoleArn(x?.role_arn));
    this.loading.clusterRoles = false;
  }

  async onVpcChange() {
    this.form.subnet_ids = [];
    this.form.security_group_ids = [];
    this.options.subnets = [];
    this.options.securityGroups = [];
    if (this.showCreateSecurityGroupForm && !this.createSecurityGroupForm?.vpc_id) {
      this.createSecurityGroupForm.vpc_id = this.form.vpc_id || "";
    }
    if (!this.form.vpc_id) return;
    await Promise.all([this.loadSubnets(), this.loadSecurityGroups()]);
  }

  async loadSubnets() {
    this.loading.subnets = true;
    const url = this.buildApiUrl(
      `apis/ec2-config-2/api/ec2/subnets?account_id=${this.accountId}&region=${this.region}&vpc_id=${this.form.vpc_id}`
    );
    const r = await this.get(url);
    this.options.subnets = r?.subnets || [];
    this.loading.subnets = false;
  }

  async loadSecurityGroups() {
    this.loading.securityGroups = true;
    const url = this.buildApiUrl(
      `apis/ec2-config-2/api/ec2/security-groups?account_id=${this.accountId}&region=${this.region}&vpc_id=${this.form.vpc_id}`
    );
    const r = await this.get(url);
    this.options.securityGroups = r?.security_groups || [];
    this.loading.securityGroups = false;
  }

  addTag() {
    this.form.tags.push({ key: "", value: "" });
  }

  removeTag(index: number) {
    if (this.form.tags.length === 1) {
      this.form.tags[0] = { key: "", value: "" };
      return;
    }
    this.form.tags.splice(index, 1);
  }

  next() {
    this.formError = "";
    if (!this.canContinueStep(this.step)) {
      this.formError = "Please complete the required fields for this step.";
      return;
    }
    if (this.step === 4) {
      this.goToReview();
      return;
    }
    this.step += 1;
  }

  back() {
    if (this.loading.submit || this.loading.pricing || this.loading.createSecurityGroup) return;
    this.formError = "";
    this.saveError = "";
    this.step = Math.max(1, this.step - 1);
  }

  requestClose() {
    if (this.loading.submit || this.loading.pricing || this.loading.createSecurityGroup) return;
    this.close.emit();
  }

  openCreateSecurityGroupForm() {
    this.showCreateSecurityGroupForm = true;
    this.createSecurityGroupStep = 1;
    this.createSecurityGroupSubmitAttempted = false;
    this.createSecurityGroupForm = this.getDefaultCreateSecurityGroupForm(
      this.form?.vpc_id || this.createSecurityGroupForm?.vpc_id || ""
    );
  }

  closeCreateSecurityGroupForm() {
    this.showCreateSecurityGroupForm = false;
    this.createSecurityGroupStep = 1;
    this.createSecurityGroupSubmitAttempted = false;
    this.loading.createSecurityGroup = false;
    this.createSecurityGroupForm = this.getDefaultCreateSecurityGroupForm(this.form?.vpc_id || "");
  }

  previousCreateSecurityGroupStep() {
    if (this.createSecurityGroupStep <= 1) {
      return;
    }

    this.createSecurityGroupStep -= 1;
    this.createSecurityGroupSubmitAttempted = false;
  }

  nextCreateSecurityGroupStep() {
    this.createSecurityGroupSubmitAttempted = true;
    if (!this.isCreateSecurityGroupStepValid(this.createSecurityGroupStep)) {
      this.notifier.alert(
        "Warning",
        "",
        this.getCreateSecurityGroupStepValidationMessage(this.createSecurityGroupStep),
        "warning",
        3500
      );
      return;
    }

    if (this.createSecurityGroupStep < this.createSecurityGroupStepLabels.length) {
      this.createSecurityGroupStep += 1;
      this.createSecurityGroupSubmitAttempted = false;
    }
  }

  addCreateSecurityGroupRule(direction: "inbound" | "outbound") {
    const key = direction === "inbound" ? "inbound_rules" : "outbound_rules";
    this.createSecurityGroupForm[key] = [
      ...this.createSecurityGroupForm[key],
      this.createSecurityGroupRule(direction),
    ];
  }

  removeCreateSecurityGroupRule(direction: "inbound" | "outbound", index: number) {
    const key = direction === "inbound" ? "inbound_rules" : "outbound_rules";
    if (!this.createSecurityGroupForm[key]?.length) {
      return;
    }

    if (this.createSecurityGroupForm[key].length === 1) {
      this.createSecurityGroupForm[key] = [this.createSecurityGroupRule(direction)];
      return;
    }

    this.createSecurityGroupForm[key] = this.createSecurityGroupForm[key].filter(
      (_: CreateSecurityGroupRuleRow, rowIndex: number) => rowIndex !== index
    );
  }

  onCreateSecurityGroupRuleProtocolChange(rule: CreateSecurityGroupRuleRow) {
    if (!this.usesCreateSecurityGroupRulePort(rule.protocol)) {
      rule.port = "-";
      return;
    }

    if (!this.isCreateSecurityGroupPortValueValid(rule.port)) {
      rule.port = rule.protocol === "UDP" ? "53" : "443";
    }
  }

  usesCreateSecurityGroupRulePort(protocol: string): boolean {
    return !["all", "-1", "icmp"].includes((protocol || "").toString().trim().toLowerCase());
  }

  isCreateSecurityGroupRuleFlagged(row: CreateSecurityGroupRuleRow): boolean {
    if (!this.isCreateSecurityGroupWorldTarget(row.target)) {
      return false;
    }

    const protocol = (row.protocol || "").toString().trim().toLowerCase();
    if (protocol === "all") {
      return true;
    }

    if (!this.usesCreateSecurityGroupRulePort(protocol)) {
      return false;
    }

    const portRange = this.parseCreateSecurityGroupPortRange(row.port);
    if (!portRange) {
      return false;
    }

    return (
      (portRange.from <= 22 && portRange.to >= 22) ||
      (portRange.from <= 3389 && portRange.to >= 3389)
    );
  }

  isCreateSecurityGroupRuleValid(
    row: CreateSecurityGroupRuleRow,
    direction: "inbound" | "outbound"
  ): boolean {
    const protocol = (row.protocol || "").toString().trim();
    if (!protocol) {
      return false;
    }

    if (
      this.usesCreateSecurityGroupRulePort(protocol) &&
      !this.isCreateSecurityGroupPortValueValid(row.port)
    ) {
      return false;
    }

    if (!this.isCreateSecurityGroupTargetValid(row.target)) {
      return false;
    }

    return !!(row.description || "").toString().trim();
  }

  getCreateSecurityGroupRuleValidationMessage(
    row: CreateSecurityGroupRuleRow,
    direction: "inbound" | "outbound"
  ): string {
    const protocol = (row.protocol || "").toString().trim();
    const targetLabel = direction === "inbound" ? "Source" : "Destination";

    if (!protocol) {
      return "Protocol is required.";
    }

    if (
      this.usesCreateSecurityGroupRulePort(protocol) &&
      !this.isCreateSecurityGroupPortValueValid(row.port)
    ) {
      return "Enter a valid port or port range between 0 and 65535.";
    }

    if (!this.isCreateSecurityGroupTargetValid(row.target)) {
      return `${targetLabel} must be a CIDR block, security group id, or prefix list id.`;
    }

    if (!(row.description || "").toString().trim()) {
      return "Description is required.";
    }

    return "";
  }

  canCreateSecurityGroup(): boolean {
    return !!(
      this.isCreateSecurityGroupStepValid(this.createSecurityGroupStepLabels.length) &&
      !this.loading.createSecurityGroup
    );
  }

  getCreateSecurityGroupVpcLabel(vpcId: string): string {
    const vpc = (this.options.vpcs || []).find((item: any) => item?.vpc_id === vpcId);
    if (!vpc) {
      return vpcId || "-";
    }

    const vpcName = this.normalize(vpc?.vpc_name ?? vpc?.name);
    const cidrBlock = this.normalize(vpc?.cidr_block ?? vpc?.cidr ?? vpc?.ipv4_cidr_block);

    if (vpcName && cidrBlock) {
      return `${vpcName} - ${cidrBlock}`;
    }

    if (vpcName) {
      return vpcName;
    }

    if (cidrBlock) {
      return cidrBlock;
    }

    return vpc?.vpc_id || vpcId || "-";
  }

  getCreateSecurityGroupRuleCountLabel(direction: "inbound" | "outbound"): string {
    const count = (direction === "inbound"
      ? this.createSecurityGroupForm?.inbound_rules
      : this.createSecurityGroupForm?.outbound_rules
    )?.length || 0;

    return `${count} rule${count === 1 ? "" : "s"}`;
  }

  formatCreateSecurityGroupRuleForReview(
    row: CreateSecurityGroupRuleRow,
    direction: "inbound" | "outbound"
  ): string {
    const protocol = (row.protocol || "").toString().trim().toUpperCase();
    const port = this.usesCreateSecurityGroupRulePort(row.protocol)
      ? (row.port || "").toString().trim()
      : "-";
    const target = (row.target || "").toString().trim();
    const description = (row.description || "").toString().trim();
    const arrow = direction === "inbound" ? "<-" : "->";

    return `${protocol} ${port} ${arrow} ${target}${description ? ` (${description})` : ""}`;
  }

  private canContinueStep(step: number): boolean {
    const asText = (v: any) => (v ?? "").toString().trim();
    if (step === 1) return !!(asText(this.form.cluster_name) && asText(this.form.launch_justification));
    if (step === 2) return !!(this.form.vpc_id && this.form.subnet_ids?.length && this.form.security_group_ids?.length);
    if (step === 3) return !!(asText(this.form.cluster_role_arn) && asText(this.form.kubernetes_version));
    if (step === 4) return (this.form.tags || []).every((t: any) => !asText(t?.key) || !!asText(t?.value));
    return true;
  }

  private canSubmit(): boolean {
    return !!(
      this.accountId &&
      this.region &&
      this.getRequesterId() &&
      this.getRequesterEmail() &&
      this.canContinueStep(1) &&
      this.canContinueStep(2) &&
      this.canContinueStep(3) &&
      this.canContinueStep(4)
    );
  }

  private isServiceLinkedRoleArn(arn: any): boolean {
    const v = (arn || "").toString();
    return v.includes(":role/aws-service-role/") || v.includes("/aws-service-role/");
  }

  private buildCreateSecurityGroupTagsPayload() {
    const environment = (this.createSecurityGroupForm?.environment || "").toString().trim();
    return environment ? { Environment: environment } : {};
  }

  private getCreateSecurityGroupApiPath() {
    return "apis/ec2-get-master-data/security-groups";
  }

  private getCreateSecurityGroupRulesApiPath(groupId: string) {
    return `apis/ec2-get-master-data/security-groups/${groupId}/rules`;
  }

  private normalizeCreateSecurityGroupProtocol(protocol: string): string {
    const normalizedProtocol = (protocol || "").toString().trim().toLowerCase();
    if (normalizedProtocol === "all") {
      return "-1";
    }
    return normalizedProtocol;
  }

  private getCreateSecurityGroupTargetType(target: string): SecurityGroupRuleTargetType | null {
    const normalizedTarget = (target || "").toString().trim();
    if (!normalizedTarget) {
      return null;
    }

    if (this.isValidSecurityGroupReference(normalizedTarget)) {
      return "referenced_group_ids";
    }

    if (this.isValidPrefixList(normalizedTarget)) {
      return "prefix_list_ids";
    }

    if (normalizedTarget.includes("/")) {
      if (this.isValidCidr(normalizedTarget, "cidr_ipv6")) {
        return "cidr_ipv6";
      }
      if (this.isValidCidr(normalizedTarget, "cidr_ipv4")) {
        return "cidr_ipv4";
      }
    }

    return null;
  }

  private buildSecurityGroupRulePayload(row: SecurityGroupRulePayloadRow): SecurityGroupApiRulePayload {
    const normalizedProtocol = this.normalizeCreateSecurityGroupProtocol(row.protocol);
    const normalizedTarget = (row.target_value || "").toString().trim();
    const payload: SecurityGroupApiRulePayload = {
      ip_protocol: normalizedProtocol,
      cidr_ipv4: row.target_type === "cidr_ipv4" ? [normalizedTarget] : [],
      cidr_ipv6: row.target_type === "cidr_ipv6" ? [normalizedTarget] : [],
      prefix_list_ids: row.target_type === "prefix_list_ids" ? [normalizedTarget] : [],
      referenced_group_ids: row.target_type === "referenced_group_ids" ? [normalizedTarget] : [],
    };

    if (this.usesCreateSecurityGroupRulePort(normalizedProtocol)) {
      payload.from_port = Number(row.from_port);
      payload.to_port = Number(row.to_port);
    }

    return payload;
  }

  private serializeCreateSecurityGroupRule(row: CreateSecurityGroupRuleRow): SecurityGroupApiRulePayload {
    const targetType = this.getCreateSecurityGroupTargetType(row.target);
    const portRange = this.usesCreateSecurityGroupRulePort(row.protocol)
      ? this.parseCreateSecurityGroupPortRange(row.port)
      : null;

    return this.buildSecurityGroupRulePayload({
      protocol: row.protocol,
      from_port: portRange?.from ?? null,
      to_port: portRange?.to ?? null,
      target_type: targetType || "cidr_ipv4",
      target_value: (row.target || "").toString().trim(),
    });
  }

  private buildCreateSecurityGroupRequestPayload() {
    return {
      account_id: this.accountId,
      region: this.region,
      group_name: (this.createSecurityGroupForm?.group_name || "").toString().trim(),
      description: (this.createSecurityGroupForm?.description || "").toString().trim(),
      vpc_id: (this.createSecurityGroupForm?.vpc_id || "").toString().trim(),
      tags: this.buildCreateSecurityGroupTagsPayload(),
    };
  }

  private isDefaultAllOutboundRule(row: CreateSecurityGroupRuleRow): boolean {
    const normalizedProtocol = this.normalizeCreateSecurityGroupProtocol(row?.protocol || "");
    const normalizedTarget = (row?.target || "").toString().trim();

    return normalizedProtocol === "-1" && normalizedTarget === "0.0.0.0/0";
  }

  private shouldSkipAuthorizeEgress(): boolean {
    const outboundRules = this.createSecurityGroupForm?.outbound_rules || [];
    return outboundRules.length === 1 && this.isDefaultAllOutboundRule(outboundRules[0]);
  }

  private buildDefaultCreateSecurityGroupEgressRevokePayload(): SecurityGroupApiRulePayload[] {
    return [
      this.buildSecurityGroupRulePayload({
        protocol: "all",
        from_port: null,
        to_port: null,
        target_type: "cidr_ipv4",
        target_value: "0.0.0.0/0",
      }),
    ];
  }

  private buildCreateSecurityGroupRulesUpdatePayload(): CreateSecurityGroupRulesUpdatePayload {
    const shouldSkipAuthorizeEgress = this.shouldSkipAuthorizeEgress();

    return {
      account_id: this.accountId,
      region: this.region,
      authorize_ingress: (this.createSecurityGroupForm?.inbound_rules || []).map((row) =>
        this.serializeCreateSecurityGroupRule(row)
      ),
      revoke_ingress: [],
      authorize_egress: shouldSkipAuthorizeEgress
        ? []
        : (this.createSecurityGroupForm?.outbound_rules || []).map((row) =>
            this.serializeCreateSecurityGroupRule(row)
          ),
      revoke_egress: shouldSkipAuthorizeEgress
        ? []
        : this.buildDefaultCreateSecurityGroupEgressRevokePayload(),
    };
  }

  private isCreateSecurityGroupStepValid(step: number): boolean {
    switch (step) {
      case 1:
        return this.hasCreateSecurityGroupBasicDetails();
      case 2:
        return this.hasValidCreateSecurityGroupRules("inbound");
      case 3:
        return this.hasValidCreateSecurityGroupRules("outbound");
      case 4:
        return !!(this.createSecurityGroupForm?.environment || "").toString().trim();
      case 5:
        return (
          this.hasCreateSecurityGroupBasicDetails() &&
          this.hasValidCreateSecurityGroupRules("inbound") &&
          this.hasValidCreateSecurityGroupRules("outbound") &&
          !!(this.createSecurityGroupForm?.environment || "").toString().trim()
        );
      default:
        return false;
    }
  }

  private getCreateSecurityGroupStepValidationMessage(step: number): string {
    switch (step) {
      case 1:
        return "Please complete the basic security group details before continuing.";
      case 2:
        return "Please fix the inbound rule details before continuing.";
      case 3:
        return "Please fix the outbound rule details before continuing.";
      case 4:
        return "Please choose an environment tag before continuing.";
      case 5:
        return "Please complete the security group wizard before creating.";
      default:
        return "Please review the security group details.";
    }
  }

  private hasCreateSecurityGroupBasicDetails(): boolean {
    return !!(
      (this.createSecurityGroupForm?.group_name || "").toString().trim() &&
      (this.createSecurityGroupForm?.description || "").toString().trim() &&
      (this.createSecurityGroupForm?.vpc_id || "").toString().trim()
    );
  }

  private hasValidCreateSecurityGroupRules(direction: "inbound" | "outbound"): boolean {
    const rows =
      direction === "inbound"
        ? this.createSecurityGroupForm?.inbound_rules || []
        : this.createSecurityGroupForm?.outbound_rules || [];

    return !!rows.length && rows.every((row: CreateSecurityGroupRuleRow) =>
      this.isCreateSecurityGroupRuleValid(row, direction)
    );
  }

  private isCreateSecurityGroupPortValueValid(port: string): boolean {
    return !!this.parseCreateSecurityGroupPortRange(port);
  }

  private parseCreateSecurityGroupPortRange(port: string) {
    const normalizedPort = (port || "").toString().trim();
    if (!/^\d{1,5}(-\d{1,5})?$/.test(normalizedPort)) {
      return null;
    }

    const [fromRaw, toRaw] = normalizedPort.split("-");
    const from = Number(fromRaw);
    const to = Number(toRaw ?? fromRaw);
    if (
      !Number.isInteger(from) ||
      !Number.isInteger(to) ||
      from < 0 ||
      from > 65535 ||
      to < 0 ||
      to > 65535 ||
      from > to
    ) {
      return null;
    }

    return { from, to };
  }

  private isCreateSecurityGroupTargetValid(value: string): boolean {
    const normalizedValue = (value || "").toString().trim();
    if (!normalizedValue) {
      return false;
    }

    if (this.isValidSecurityGroupReference(normalizedValue)) {
      return true;
    }

    if (this.isValidPrefixList(normalizedValue)) {
      return true;
    }

    if (normalizedValue.includes("/")) {
      return this.isValidCidr(
        normalizedValue,
        normalizedValue.includes(":") ? "cidr_ipv6" : "cidr_ipv4"
      );
    }

    return false;
  }

  private isCreateSecurityGroupWorldTarget(target: string): boolean {
    const normalizedTarget = (target || "").toString().trim();
    return normalizedTarget === "0.0.0.0/0" || normalizedTarget === "::/0";
  }

  private isValidCidr(value: string, type: "cidr_ipv4" | "cidr_ipv6") {
    const normalizedValue = (value || "").toString().trim();
    if (!normalizedValue) {
      return false;
    }

    if (type === "cidr_ipv4") {
      return /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}\/([0-9]|[1-2]\d|3[0-2])$/.test(
        normalizedValue
      );
    }

    return /^[0-9a-fA-F:]+\/([0-9]|[1-9]\d|1[01]\d|12[0-8])$/.test(normalizedValue);
  }

  private isValidSecurityGroupReference(value: string) {
    return /^sg-[a-zA-Z0-9]+$/.test((value || "").toString().trim());
  }

  private isValidPrefixList(value: string) {
    return /^pl-[a-zA-Z0-9]+$/.test((value || "").toString().trim());
  }

  private getSecurityGroupOptionId(option: any): string {
    return this.normalize(option?.security_group_id ?? option?.group_id ?? option?.sg_id ?? option?.id);
  }

  private getSecurityGroupOptionName(option: any): string {
    return this.normalize(option?.security_group_name ?? option?.group_name ?? option?.sg_name ?? option?.name);
  }

  private selectCreatedSecurityGroup(groupId: string, groupName: string) {
    const matched = (this.options.securityGroups || []).find((option: any) => {
      const optionId = this.getSecurityGroupOptionId(option);
      const optionName = this.getSecurityGroupOptionName(option);
      return (groupId && optionId === groupId) || (groupName && optionName === groupName);
    });

    const matchedId = this.getSecurityGroupOptionId(matched);
    if (matchedId && !this.form.security_group_ids.includes(matchedId)) {
      this.form.security_group_ids = [...this.form.security_group_ids, matchedId];
    }
  }

  private buildCreatedSecurityGroupOption(groupId: string) {
    const vpcId = (this.createSecurityGroupForm?.vpc_id || "").toString().trim();
    const vpc = (this.options.vpcs || []).find((item: any) => item?.vpc_id === vpcId);

    return {
      security_group_id: groupId,
      group_id: groupId,
      sg_id: groupId,
      security_group_name: (this.createSecurityGroupForm?.group_name || "").toString().trim(),
      group_name: (this.createSecurityGroupForm?.group_name || "").toString().trim(),
      description: (this.createSecurityGroupForm?.description || "").toString().trim(),
      vpc_id: vpcId,
      vpc_name: this.normalize(vpc?.vpc_name ?? vpc?.name),
      cidr_block: this.normalize(vpc?.cidr_block ?? vpc?.cidr ?? vpc?.ipv4_cidr_block),
      tags: this.buildCreateSecurityGroupTagsPayload(),
    };
  }

  private hasSecurityGroupOption(groupId: string) {
    return (this.options.securityGroups || []).some(
      (option: any) => this.getSecurityGroupOptionId(option) === groupId
    );
  }

  private async refreshSecurityGroupsAfterCreate(option: any) {
    const vpcId = this.normalize(option?.vpc_id);
    if (this.form.vpc_id !== vpcId) {
      this.form.vpc_id = vpcId;
      await this.onVpcChange();
    } else {
      for (let attempt = 0; attempt < 3; attempt++) {
        await this.loadSecurityGroups();
        if (this.hasSecurityGroupOption(this.getSecurityGroupOptionId(option))) {
          break;
        }
        await this.wait(400 * (attempt + 1));
      }
    }

    if (!this.hasSecurityGroupOption(this.getSecurityGroupOptionId(option))) {
      this.options.securityGroups = [option, ...(this.options.securityGroups || [])];
    }

    this.selectCreatedSecurityGroup(
      this.getSecurityGroupOptionId(option),
      this.getSecurityGroupOptionName(option)
    );
  }

  async goToReview() {
    if (!this.canSubmit() || this.loading.pricing) return;
    if (this.isServiceLinkedRoleArn(this.form.cluster_role_arn)) {
      this.formError = "Cluster Role ARN cannot be an AWS service-linked role.";
      return;
    }
    this.step = 5;
    this.loading.pricing = true;
    this.pricingError = "";
    this.clusterPricing = null;
    try {
      const result: any = await this.apiServer.postDataPromis(
        this.buildApiUrl("apis/eks_launch/api/eks/cluster-pricing"),
        { region: this.region, support_tier: "STANDARD" },
        this.headers()
      );
      if (!result || typeof result === "string") throw new Error("Invalid response");
      this.clusterPricing = result;
    } catch {
      this.pricingError = "Unable to fetch cluster pricing right now.";
    } finally {
      this.loading.pricing = false;
    }
  }

  buildRequestBody() {
    return {
      requester_id: this.getRequesterId(),
      requester_email: this.getRequesterEmail(),
      launch_justification: this.form.launch_justification.trim(),
      jira_project_key: this.normalize(localStorage.getItem("swayam_jira_project_key") || localStorage.getItem("jira_project_key")),
      jira_organization_key: this.normalize(
        localStorage.getItem("swayam_jira_org_name") || localStorage.getItem("jira_organization_key") || localStorage.getItem("swayam_jira_org_key")
      ),
      request_payload: {
        aws_account_id: this.accountId,
        region: this.region,
        cluster_name: this.form.cluster_name.trim(),
        vpc_id: this.form.vpc_id,
        cluster_role_arn: this.form.cluster_role_arn.trim(),
        subnet_ids: this.form.subnet_ids.map((x: string) => x.trim()).filter((x: string) => !!x),
        security_group_ids: this.form.security_group_ids.map((x: string) => x.trim()).filter((x: string) => !!x),
        kubernetes_version: this.form.kubernetes_version.trim(),
        endpoint_public_access: !!this.form.endpoint_public_access,
        endpoint_private_access: !!this.form.endpoint_private_access,
        tags: this.form.tags
          .map((t: any) => ({ key: (t.key || "").trim(), value: (t.value || "").trim() }))
          .filter((t: any) => t.key && t.value),
      },
    };
  }

  async submitCreateSecurityGroup() {
    this.createSecurityGroupSubmitAttempted = true;
    if (!this.canCreateSecurityGroup()) {
      this.notifier.alert(
        "Warning",
        "",
        "Please complete the required security group details before creating.",
        "warning",
        3000
      );
      return;
    }

    this.loading.createSecurityGroup = true;
    const createResult = await this.post(
      this.buildApiUrl(this.getCreateSecurityGroupApiPath()),
      this.buildCreateSecurityGroupRequestPayload()
    );

    const createdGroupId = this.normalize(
      createResult?.group_id ?? createResult?.security_group_id ?? createResult?.data?.group_id
    );

    if (!this.isSuccessResponse(createResult) || !createdGroupId) {
      this.loading.createSecurityGroup = false;
      this.notifier.alert(
        "Error",
        "",
        createResult?.error ||
          createResult?.message ||
          createResult?.msg ||
          "Unable to create the security group.",
        "error",
        5000
      );
      return;
    }

    const rulesResult = await this.put(
      this.buildApiUrl(this.getCreateSecurityGroupRulesApiPath(createdGroupId)),
      this.buildCreateSecurityGroupRulesUpdatePayload()
    );

    if (!this.isSuccessResponse(rulesResult)) {
      const createdSecurityGroup = this.buildCreatedSecurityGroupOption(createdGroupId);
      await this.refreshSecurityGroupsAfterCreate(createdSecurityGroup);
      this.loading.createSecurityGroup = false;
      this.closeCreateSecurityGroupForm();
      this.notifier.alert(
        "Warning",
        "",
        rulesResult?.error ||
          rulesResult?.message ||
          rulesResult?.msg ||
          `Security group ${createdGroupId} was created, but applying the rules failed.`,
        "warning",
        7000
      );
      return;
    }

    const createdSecurityGroup = this.buildCreatedSecurityGroupOption(createdGroupId);
    await this.refreshSecurityGroupsAfterCreate(createdSecurityGroup);
    this.loading.createSecurityGroup = false;
    this.closeCreateSecurityGroupForm();
    this.notifier.alert(
      "Success",
      "",
      rulesResult?.message || rulesResult?.msg || "Security group created successfully.",
      "success",
      4000
    );
  }

  async submitRequest() {
    this.saveError = "";
    if (!this.canSubmit() || this.loading.submit || !this.clusterPricing || this.pricingError) {
      this.formError = "Please complete the request before submitting.";
      return;
    }
    this.loading.submit = true;
    try {
      const result: any = await this.apiServer.postDataPromis(
        this.buildApiUrl("apis/eks_launch/api/eks/save-request"),
        this.buildRequestBody(),
        this.headers()
      );
      if (!result || typeof result === "string" || result.detail || result.error || (result.status !== undefined && result.status !== "1" && result.status !== 1) || (result.s !== undefined && result.s !== "1" && result.s !== 1)) {
        let errorMsg = result?.message || result?.msg || (Array.isArray(result?.detail) ? result.detail[0]?.msg : null) || result?.error || "Failed to submit EKS request.";
        if (typeof errorMsg === "string" && errorMsg.startsWith("Value error, ")) {
          errorMsg = errorMsg.replace("Value error, ", "");
        }
        throw new Error(errorMsg);
      }
      this.saveSuccessMessage = result.message || "EKS request submitted successfully.";
      this.notifier.alert("Success", "", this.saveSuccessMessage, "success", 4000);
      this.submitted.emit();
      this.close.emit();
    } catch (err: any) {
      this.saveError = err?.message || "Failed to submit EKS request.";
      this.notifier.alert("Error", "", this.saveError, "error", 4000);
    } finally {
      this.loading.submit = false;
    }
  }

  private ensureDropdownVisible(
    select: NgSelectComponent,
    container: "request" | "securityGroup"
  ) {
    const scrollContainer = this.getScrollContainer(container);
    const triggerElement = select?.element as HTMLElement | null;
    const panelElement = this.getDropdownPanelElement(select);

    if (!scrollContainer || !triggerElement || !panelElement) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const triggerRect = triggerElement.getBoundingClientRect();
    const panelRect = panelElement.getBoundingClientRect();
    const verticalPadding = 12;
    const visibleTop = containerRect.top + verticalPadding;
    const visibleBottom = containerRect.bottom - verticalPadding;
    const contentTop = Math.min(triggerRect.top, panelRect.top);
    const contentBottom = Math.max(triggerRect.bottom, panelRect.bottom);

    const isFullyVisible =
      triggerRect.top >= visibleTop &&
      triggerRect.bottom <= visibleBottom &&
      panelRect.top >= visibleTop &&
      panelRect.bottom <= visibleBottom;

    if (isFullyVisible) {
      return;
    }

    let delta = 0;
    if (contentTop < visibleTop) {
      delta = contentTop - visibleTop;
    } else if (contentBottom > visibleBottom) {
      delta = contentBottom - visibleBottom;
    }

    if (Math.abs(delta) < 1) {
      return;
    }

    const nextScrollTop = this.clamp(
      scrollContainer.scrollTop + delta,
      0,
      scrollContainer.scrollHeight - scrollContainer.clientHeight
    );

    if (Math.abs(nextScrollTop - scrollContainer.scrollTop) < 1) {
      return;
    }

    scrollContainer.scrollTo({
      top: nextScrollTop,
      behavior: "smooth",
    });

    window.setTimeout(() => {
      select.dropdownPanel?.adjustPosition();
    }, 260);
  }

  private getScrollContainer(container: "request" | "securityGroup") {
    return container === "securityGroup"
      ? this.securityGroupModalBody?.nativeElement || null
      : this.eksRequestModalBody?.nativeElement || null;
  }

  private getDropdownPanelElement(select: NgSelectComponent): HTMLElement | null {
    const scrollElement = select?.dropdownPanel?.scrollElementRef?.nativeElement as HTMLElement | undefined;
    return (scrollElement?.closest("ng-dropdown-panel") as HTMLElement | null) || scrollElement || null;
  }

  private clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
  }
}
